import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'

// Self-delete hooks (#4). All hit /api/account/* — the user can only
// act on their own account; server uses req.user.userId, no body id.

export interface CustodialDependent {
  id: number
  firstName: string
  lastName: string
  isMinor: boolean
}

export interface MyDependentsResponse {
  count: number
  dependents: CustodialDependent[]
}

// Used by the confirmation modal so we can show specific dependent
// names ("Asha (minor), Ravi…") rather than a bare count. staleTime is
// short — the count can change if the user adds a dependent in another
// tab and then opens the delete dialog.
export function useMyDependents() {
  return useQuery({
    queryKey: ['account', 'dependents'],
    queryFn: async () => {
      const { data } = await apiClient.get<MyDependentsResponse>('/api/account/dependents')
      return data
    },
    staleTime: 30_000,
  })
}

export interface TombstoneArgs {
  mode: 'tombstone' | 'erase-30d'
  reason?: string
}

export interface TombstoneResult {
  success: boolean
  mode: 'tombstone' | 'erase-30d'
  ownerTombstoned: boolean
  dependentsTombstoned: number
  postsDeleted: number
  pendingRequestsCancelled: number
}

// Tombstones the account. On success the caller must clear local auth
// state and route to a confirmation screen — subsequent requests will
// be rejected by rejectInactive with ACCOUNT_DELETED.
export function useTombstoneAccount() {
  return useMutation({
    mutationFn: async (args: TombstoneArgs) => {
      const { data } = await apiClient.post<TombstoneResult>('/api/account/tombstone', args)
      return data
    },
  })
}

export interface CancelEraseResult {
  success: boolean
  cancelled: boolean
}

// Clears erase_requested_at. Used by the persistent erase-pending banner
// (CancelEraseBanner). Invalidates the React Query cache for /me so the
// authStore.user.eraseRequestedAt flips back to null on next refresh.
export function useCancelErase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (reason?: string) => {
      const { data } = await apiClient.post<CancelEraseResult>('/api/account/cancel-erase', { reason })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}
